import Papa from "papaparse";
import pluralize from "pluralize";
import { generateCsvFileName } from "../../../../../hooks/useFileNaming";
import {
  COMPLEX_ENTITY_2ND_COLUMN_MAP,
  COMPLEX_ENTITY_3RD_COLUMN_MAP,
  ComplexEntityTypes,
} from "../../../../../utils/constants";
import { getPrettyDate } from "../../../../../utils/timestamp";
import { getEntityTypeDisplayName } from "../../../../../utils/utils";
import { extractTypeFromEntityId, extractUniqueIdFromEntityId } from "./utils";

export const csvExportFile = (data, name) => {
  const csv = Papa.unparse(data);
  const csvData = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const csvURL = window.URL.createObjectURL(csvData);
  let tempLink = document.createElement("a");
  tempLink.href = csvURL;
  tempLink.setAttribute("download", generateCsvFileName(name));
  tempLink.click();
};

export const csvExportFileByType = (data, type) => {
  csvExportFile(data, `misconfigured_entities_${pluralize(type)}`);
};

export function affectedEntitiesToCsv(
  controlId = null,
  entities,
  selectedEntitiesIds = null,
  misconfiguration = null,
  labelIdToNameMap = null
) {
  let selectedEntities = entities;
  if (selectedEntitiesIds?.length > 0) {
    selectedEntities = selectedEntities.filter((entity) =>
      selectedEntitiesIds.includes(entity.entity_id)
    );
  }

  if (!selectedEntities || selectedEntities.length === 0) {
    return;
  }

  const headersRow = [`Type`, `Name`];

  const firstEntity = selectedEntities[0];
  const firstEntityType =
    firstEntity?.entity_type || extractTypeFromEntityId(firstEntity?.entity_id);

  if (firstEntityType in COMPLEX_ENTITY_2ND_COLUMN_MAP) {
    headersRow.push(COMPLEX_ENTITY_2ND_COLUMN_MAP[firstEntityType].columnName);
  }

  if (firstEntityType in COMPLEX_ENTITY_3RD_COLUMN_MAP) {
    headersRow.push(COMPLEX_ENTITY_3RD_COLUMN_MAP[firstEntityType].columnName);
  }

  headersRow.push("First Discovered");
  headersRow.push("Label");

  const csvRows = selectedEntities.map((entity) => {
    const entityType =
      entity?.entity_type || extractTypeFromEntityId(entity.entity_id);
    let row = [getEntityTypeDisplayName(entityType), entity.display_name];

    if (entityType in COMPLEX_ENTITY_2ND_COLUMN_MAP) {
      const propertyName = COMPLEX_ENTITY_2ND_COLUMN_MAP[entityType].prop;

      let value;
      if (
        entityType === ComplexEntityTypes.HOST &&
        propertyName === "last_seen"
      ) {
        const ts = entity?.last_collected_timestamp?.timestamp_utc;
        value = ts ? getPrettyDate(ts, false) : "-";
      } else {
        value =
          entity?.[propertyName] ?? entity?.entity_data?.[propertyName] ?? "-";
      }

      row.push(value);
    }

    if (entityType in COMPLEX_ENTITY_3RD_COLUMN_MAP) {
      const propertyName = COMPLEX_ENTITY_3RD_COLUMN_MAP[entityType].prop;
      const value =
        entity?.[propertyName] ?? entity?.entity_data?.[propertyName] ?? "-";
      row.push(value);
    }

    let firstSeenTs = null;
    if (misconfiguration) {
      const affectedEntity = misconfiguration.affected_entities?.find(
        (ae) => ae.entity_id === entity.entity_id
      );
      firstSeenTs = affectedEntity?.first_seen?.timestamp_utc;
    } else {
      firstSeenTs = entity?.first_seen?.timestamp_utc;
    }
    const firstSeenValue = firstSeenTs
      ? getPrettyDate(firstSeenTs, false)
      : "N/A";
    row.push(firstSeenValue);

    let labelsValue = "-";
    if (entity?.labels && entity.labels.length > 0) {
      if (labelIdToNameMap) {
        const labelNames = entity.labels
          .map((labelId) => labelIdToNameMap[labelId])
          .filter((name) => name !== undefined);
        labelsValue = labelNames.length > 0 ? labelNames.join(", ") : "-";
      } else {
        labelsValue = entity.labels.join(", ");
      }
    }
    row.push(labelsValue);

    return row;
  });
  const csvData = [headersRow, ...csvRows];
  const title = controlId
    ? `control_#${controlId}_affected_entities`
    : "affected_entities";
  csvExportFile(csvData, title);
}

export function extractMisconfigurationsCsvHeaders(misconfigurations) {
  if (!misconfigurations || misconfigurations.length === 0) {
    return [];
  }

  const misconfigurationType = extractTypeFromEntityId(
    misconfigurations[0].entity_id
  );

  let headers = [
    `${getEntityTypeDisplayName(misconfigurationType)} - Name`,
    "Evidence",
  ];

  let insertIndex = 1;
  if (misconfigurationType in COMPLEX_ENTITY_2ND_COLUMN_MAP) {
    headers.splice(
      insertIndex,
      0,
      `${getEntityTypeDisplayName(misconfigurationType)} - ${
        COMPLEX_ENTITY_2ND_COLUMN_MAP[misconfigurationType].columnName
      }`
    );
    insertIndex++;
  }

  if (misconfigurationType in COMPLEX_ENTITY_3RD_COLUMN_MAP) {
    headers.splice(
      insertIndex,
      0,
      `${getEntityTypeDisplayName(misconfigurationType)} - ${
        COMPLEX_ENTITY_3RD_COLUMN_MAP[misconfigurationType].columnName
      }`
    );
    insertIndex++;
  }

  headers.splice(insertIndex, 0, "First Discovered");

  return headers;
}

export function exportMisconfigurationsCsv(
  controlId,
  misconfigurations,
  selectedMisconfigurationIds = null,
  misconfiguredEntities = null
) {
  let misconfigurationsToExport = misconfigurations;
  if (selectedMisconfigurationIds?.length > 0) {
    misconfigurationsToExport = misconfigurationsToExport.filter(
      (misconfiguration) =>
        selectedMisconfigurationIds.includes(
          misconfiguration.misconfiguration_id
        )
    );
  }

  if (!misconfigurationsToExport || misconfigurationsToExport.length === 0) {
    return;
  }

  const headersRow = extractMisconfigurationsCsvHeaders(
    misconfigurationsToExport
  );

  let entityMap = null;
  if (misconfiguredEntities) {
    entityMap = misconfiguredEntities.reduce((acc, entity) => {
      acc[entity.entity_id] = entity;
      return acc;
    }, {});
  }

  const csvRows = misconfigurationsToExport.map((misconfiguration) => {
    let mappedRow = [
      entityMap
        ? entityMap[misconfiguration.entity_id]?.display_name
        : extractUniqueIdFromEntityId(misconfiguration.entity_id),
      JSON.stringify(misconfiguration.evidence),
    ];

    let insertIndex = 1;
    if (
      misconfiguration.entity_type in COMPLEX_ENTITY_2ND_COLUMN_MAP &&
      entityMap
    ) {
      const propertyName =
        COMPLEX_ENTITY_2ND_COLUMN_MAP[misconfiguration.entity_type]?.prop;
      let value;
      if (
        misconfiguration.entity_type === ComplexEntityTypes.HOST &&
        propertyName === "last_seen"
      ) {
        const entity = entityMap[misconfiguration.entity_id];
        const ts = entity?.last_collected_timestamp?.timestamp_utc;
        value = ts ? getPrettyDate(ts, false) : "-";
      } else {
        const entity = entityMap[misconfiguration.entity_id];
        value =
          entity?.[propertyName] || entity?.entity_data?.[propertyName] || "-";
      }
      mappedRow.splice(insertIndex, 0, value);
      insertIndex++;
    }

    if (
      misconfiguration.entity_type in COMPLEX_ENTITY_3RD_COLUMN_MAP &&
      entityMap
    ) {
      const propertyName =
        COMPLEX_ENTITY_3RD_COLUMN_MAP[misconfiguration.entity_type]?.prop;
      const entity = entityMap[misconfiguration.entity_id];
      const value =
        entity?.[propertyName] || entity?.entity_data?.[propertyName] || "-";
      mappedRow.splice(insertIndex, 0, value);
      insertIndex++;
    }

    const firstSeenTs = misconfiguration?.first_seen?.timestamp_utc;
    const firstSeenValue = firstSeenTs
      ? getPrettyDate(firstSeenTs, false)
      : "N/A";
    mappedRow.splice(insertIndex, 0, firstSeenValue);

    return mappedRow;
  });

  const csvData = [headersRow, ...csvRows];
  csvExportFile(csvData, `control_#${controlId}_misconfigurations`);
}
